import { DEFAULTS } from "@/react/story/data";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useLocales } from "@/react/story/providers/LocalesContext";
import { useStats } from "@/react/story/providers/StatsContext";
import { type Content, type FlexAlign } from "@/react/story/types";
import TemplatingUtils from "@/react/story/utils/TemplatingUtils";
import TranslationUtils from "@/react/story/utils/TranslationUtils";
import type { MouseEvent } from "react";

type Props = {
    content: Content;
    tag: "h1" | "h2" | "p";
}

/**
 * `text-align` only moves lines inside the box, and a block aligned on `center` or `flex-end`
 * shrinks a one-line text's box to the text itself: an explicit alignment must move the box.
 */
const SELF_ALIGNS: Record<"left" | "center" | "right", FlexAlign> = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
};

export default function ContentText(props: Props) {
    const Locales = useLocales();
    const Items = useItems();
    const Stats = useStats();

    const parentClass = [ props.content.config.animation ? `animate__${props.content.config.animation}` : null ].filter(c => null !== c).join(" ");
    const Tag = props.tag;
    const href = props.content.config.href ?? undefined;
    const ChildTag: "a" | "span" = href ? "a" : "span";
    const childClass = [ "m-0", `px-${props.content.config.paddingX}`, `py-${props.content.config.paddingY}` ].join(" ");

    const handleLinkClick = (e: MouseEvent<HTMLElement>) => {
        const linkHref = e.currentTarget.getAttribute("href");
        if (linkHref && Items.current?.page?.id) {
            Stats.trackLinkClick(Items.current.page.id, linkHref);
        }
    };

    return (
        <div
            className={parentClass}
            style={{
                alignSelf: props.content.config.textAlign ? SELF_ALIGNS[props.content.config.textAlign] : undefined,
                animationDelay: TemplatingUtils.duration(DEFAULTS.animationDelay),
                animationDuration: TemplatingUtils.duration(DEFAULTS.animationDuration),
                ...TemplatingUtils.style(props.content.config, [ "animationDelay", "animationDuration" ], [ "fontSize", "fontFamily" ]),
            }}
        >
            <Tag
                className={childClass}
                style={{
                    ...TemplatingUtils.style(props.content.config, null, [ "animationDelay", "animationDuration", "fontSize", "fontFamily", "color" ]),
                }}
            >
                <ChildTag
                    className={`fw-${props.content.config.fontWeight || "normal"}`}
                    href={href}
                    onClick={href ? handleLinkClick : undefined}
                    style={{
                        whiteSpace: "pre-wrap",
                        ...TemplatingUtils.style(props.content.config, [ "color", "fontSize", "fontFamily" ], [ "animationDelay", "animationDuration" ]),
                    }}
                >
                    {TranslationUtils.get(props.content.config.text, Locales.current)}
                </ChildTag>
            </Tag>
        </div>
    );
}
